import type { ReactNode } from 'react';

interface FaqItem {
  question: string;
  answer: ReactNode;
}

function formatAnswer(text: string, boldKeywords: string[] = []) {
  const escapedKeywords = boldKeywords
    .filter(Boolean)
    .map((keyword) => keyword.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'));
  const keywordRegex = escapedKeywords.length > 0
    ? new RegExp(`(${escapedKeywords.join('|')})`, 'g')
    : null;
  const sentences = text
    .split(/(?<=\.)\s+/)
    .map((sentence) => sentence.trim())
    .filter(Boolean);

  return (
    <div className="space-y-2">
      {sentences.map((sentence, sentenceIndex) => {
        const parts = keywordRegex ? sentence.split(keywordRegex) : [sentence];

        return (
          <p key={`${sentence}-${sentenceIndex}`}>
            {parts.map((part, partIndex) => {
              if (keywordRegex && boldKeywords.includes(part)) {
                return <strong key={`${part}-${partIndex}`} className="font-semibold text-[#211C18]">{part}</strong>;
              }

              return part;
            })}
          </p>
        );
      })}
    </div>
  );
}

const suitKeywords = ['촬영', '본식', '맞춤 예복', '대여 예복', '제작 기간', '예약', '계약금', '잔금', '추가 비용'];
const jewelryKeywords = ['제작', '디자인', '사이즈', '다이아몬드', '감정서', 'AS', '계약', '추가 비용', '취소'];
const hanbokKeywords = ['상담', '제작', '사이즈', '예산', '디자인', '계약', 'AS', '추가 비용'];

function createFaqList<T extends { question: string; answer: string }>(items: T[], boldKeywords: string[]): FaqItem[] {
  return items.map((item) => ({
    question: item.question,
    answer: formatAnswer(item.answer, boldKeywords),
  }));
}

const suitSource = [
  {
    question: "예복은 맞춤과 대여 중 어떤 방식으로 준비하는 것이 좋나요?",
    answer:
      "촬영 때는 대여 예복을 활용하고, 본식에 맞춰 맞춤 예복을 제작하는 경우가 많습니다. 촬영일부터 본식까지 체형 변화가 거의 없을 것으로 예상된다면 촬영 전에 맞춤 예복을 준비해 두 일정에 모두 활용할 수도 있습니다. 맞춤 예복은 제작 기간이 필요하므로 촬영과 본식 일정, 체형 관리 계획을 함께 고려해 결정하는 것이 좋습니다.",
  },
  {
    question: "예복은 언제부터 준비하는 것이 좋나요?",
    answer:
      "대여 예복은 촬영이나 본식 예정일을 기준으로 약 1~2개월 전부터 알아보는 것이 좋습니다. 맞춤 예복은 상담과 원단 선택, 제작, 가봉 및 수정 과정이 필요하므로 이보다 여유 있게 준비하는 것을 권장합니다. 인기 있는 의상이나 상담 시간은 일찍 마감될 수 있으므로 일정이 확정되었다면 미리 업체에 문의해 주세요.",
  },
  {
    question: "예복 대여는 어떤 순서로 진행되나요?",
    answer:
      "일반적으로 업체 방문 및 상담, 계약, 대여복 선택, 피팅과 사이즈 조정, 의상 수령 및 반납 순서로 진행됩니다. 촬영용 대여복은 상담이나 계약 당일에 선택하는 경우가 많으며, 본식용 대여복은 본식 약 1개월 전에 최종적으로 고르는 경우가 많습니다. 구체적인 일정과 진행 방식은 업체마다 다를 수 있습니다.",
  },
  {
    question: "촬영용 예복은 보통 몇 벌 정도 대여하나요?",
    answer:
      "촬영용 예복은 일반적으로 2~3벌 정도 대여하여 색상과 스타일을 다르게 연출하는 경우가 많습니다. 다만 스튜디오에서 제공하는 의상과 신부 드레스, 한복, 캐주얼 의상 등을 함께 촬영한다면 전체 촬영 시간과 의상 교체 횟수를 고려해 대여 수량을 정하는 것이 좋습니다.",
  },
  {
    question: "예복 상담을 받으려면 예약이 필요한가요?",
    answer:
      "원활한 상담과 피팅을 위해서는 방문 전에 예약하는 것을 권장합니다. 예약 없이 방문할 수 있는 업체도 있지만, 주말이나 인기 시간대에는 대기 시간이 발생하거나 충분한 상담을 받기 어려울 수 있습니다. 상담 예약 시 촬영일과 본식일, 원하는 예복 방식, 예상 스타일을 함께 전달하면 보다 원활하게 안내받을 수 있습니다.",
  },
  {
    question: "스튜디오에 구비된 예복과 예복 전문점의 대여복은 어떤 차이가 있나요?",
    answer:
      "스튜디오에 구비된 예복은 검은색 등 기본적인 색상과 디자인 위주로 구성되어 선택의 폭이 제한적일 수 있습니다. 예복 전문점은 색상과 디자인, 사이즈가 상대적으로 다양하며 신랑님의 체형과 촬영 콘셉트에 맞춰 피팅할 수 있다는 장점이 있습니다. 스튜디오 제공 의상을 먼저 확인한 뒤 부족한 스타일만 추가로 대여하는 방법도 좋습니다.",
  },
  {
    question: "예복 대여 시 계약금과 잔금은 어떻게 결제하나요?",
    answer:
      "계약금과 잔금의 비율 및 결제 시점은 업체마다 다릅니다. 일반적으로 계약할 때 일부 금액을 계약금으로 결제하고, 대여복 선택이나 의상 수령 전후에 잔금을 결제합니다. 계약 전에는 총금액과 추가 비용, 결제 일정, 의상 변경 및 취소 규정, 반납 지연이나 훼손에 따른 비용을 함께 확인하는 것이 좋습니다.",
  },
];

const jewelrySource = [
  {
    question: "예물은 언제부터 준비하는 것이 좋나요?",
    answer:
      "예물은 상담과 디자인 선택, 제작, 사이즈 조정에 시간이 필요하므로 결혼 준비 초기에 여유 있게 알아보는 것이 좋습니다. 일반적으로 수령까지 1~2개월 정도 소요될 수 있으며, 촬영 때 착용할 예정이라면 촬영일을 기준으로 최소 2개월 전에는 상담을 시작하는 것이 안전합니다. 제작 기간은 디자인과 업체 일정에 따라 달라질 수 있습니다.",
  },
  {
    question: "예물 제작 기간은 보통 얼마나 걸리나요?",
    answer:
      "일반적으로 계약 후 수령까지 약 1~2개월 정도 소요됩니다. 맞춤 디자인이나 특수한 세팅, 사이즈 조정이 필요한 경우에는 제작 기간이 더 길어질 수 있습니다. 명절이나 연말연시처럼 공방 일정이 혼잡한 시기에는 기간이 늘어날 수 있으므로 촬영일과 본식일을 미리 전달해 주세요.",
  },
  {
    question: "예물 구성은 보통 어떻게 준비하나요?",
    answer:
      "최근에는 평소에도 착용하기 좋은 웨딩 밴드나 커플링을 중심으로 준비하는 경우가 많습니다. 필요에 따라 다이아몬드 반지, 가드링, 목걸이, 귀걸이 등을 추가할 수 있으며, 양가 부모님을 위한 혼주 예물을 함께 알아보기도 합니다. 상담 전 커플링과 다이아몬드 중 어디에 예산을 더 배정할지 미리 상의하면 구성을 정하기 수월합니다.",
  },
  {
    question: "예물 디자인을 변경하거나 맞춤 제작할 수 있나요?",
    answer:
      "업체와 디자인에 따라 기존 제품의 소재, 색상, 두께, 보석 세팅, 유무광 마감 등을 변경할 수 있습니다. 일부 업체에서는 원하는 디자인을 바탕으로 맞춤 제작도 가능합니다. 맞춤 제작은 일반 제품보다 제작 기간과 비용이 늘어날 수 있으므로 참고할 이미지와 원하는 요소를 준비해 상담받는 것이 좋습니다.",
  },
  {
    question: "예물 상담 전에 무엇을 준비하면 좋나요?",
    answer:
      "대략적인 예산과 원하는 구성, 선호하는 금속 색상, 평소 착용하는 스타일을 미리 정해두면 좋습니다. 손을 많이 사용하는 직업인지, 매일 착용할 것인지도 디자인 선택에 중요한 기준이 됩니다. 마음에 드는 디자인 사진을 준비하면 취향을 전달하기 쉽지만, 실제 착용했을 때 느낌이 다를 수 있으므로 상담 시 여러 디자인을 직접 착용해 보는 것을 권장합니다.",
  },
  {
    question: "화이트골드, 로즈골드, 옐로우골드 중 어떤 색상을 선택해야 하나요?",
    answer:
      "금속 색상은 피부 톤과 평소 착용하는 액세서리, 의상 스타일을 고려해 선택하는 것이 좋습니다. 화이트골드는 깔끔하고 현대적인 느낌, 로즈골드는 부드럽고 따뜻한 느낌, 옐로우골드는 클래식한 느낌을 줄 수 있습니다. 같은 색상이라도 디자인과 표면 마감에 따라 인상이 달라지므로 직접 착용해 비교해 주세요.",
  },
  {
    question: "다이아몬드를 선택할 때 무엇을 확인해야 하나요?",
    answer:
      "다이아몬드는 중량, 컷, 컬러, 투명도를 의미하는 4C 기준과 감정서 발급 여부를 확인하는 것이 중요합니다. 등급을 무조건 높이기보다는 예산과 실제 착용 시 보이는 크기, 빛의 반짝임 등을 함께 고려하는 것이 좋습니다. 감정서 발급 기관과 다이아몬드의 고유 번호, 교환 및 보증 조건도 계약 전에 확인해 주세요.",
  },
  {
    question: "다이아몬드 감정서는 왜 확인해야 하나요?",
    answer:
      "감정서는 다이아몬드의 중량과 컷, 컬러, 투명도 등 객관적인 등급을 확인할 수 있는 자료입니다. 구매한 다이아몬드가 상담받은 제품과 일치하는지 확인하는 데 도움이 되며, 향후 보증이나 재감정이 필요한 경우에도 활용할 수 있습니다. 감정서 제공 여부와 발급 기관은 계약 전에 확인하는 것이 좋습니다.",
  },
  {
    question: "반지 사이즈는 언제 측정하는 것이 좋나요?",
    answer:
      "반지 사이즈는 상담이나 계약 시 측정하며, 손이 지나치게 붓거나 차가운 상태는 피하는 것이 좋습니다. 손가락 굵기는 계절과 시간대, 체중 변화에 따라 달라질 수 있으므로 평소 착용감을 기준으로 선택해 주세요. 반지 폭이 넓은 디자인은 같은 호수라도 더 타이트하게 느껴질 수 있어 실제 제품과 비슷한 폭으로 측정하는 것이 좋습니다.",
  },
  {
    question: "반지 사이즈 조절은 언제든 가능한가요?",
    answer:
      "일반적인 디자인은 사이즈 조절이 가능한 경우가 많지만, 조절 가능한 범위는 디자인과 소재에 따라 다릅니다. 반지 전체에 보석이 세팅된 풀 이터니티 링이나 패턴이 이어지는 디자인은 사이즈 조절이 어렵거나 불가능할 수 있습니다. 체중 변화나 임신 등을 고려하고 있다면 계약 전에 조절 가능 범위와 비용을 확인해 주세요.",
  },
  {
    question: "예물 수령 후에는 어떤 AS를 받을 수 있나요?",
    answer:
      "업체에 따라 사이즈 조절, 세척, 광택 작업, 도금, 스톤 빠짐 수선 등의 서비스를 제공할 수 있습니다. 다만 무료 제공 기간과 횟수, 왕복 배송비, 부품 및 보석 비용은 업체별로 다릅니다. 계약 전에 AS 가능 항목과 유무상 기준, 보증 기간을 확인하고 관련 내용을 계약서에 기재해 두는 것이 좋습니다.",
  },
  {
    question: "예물 계약 후 디자인을 변경하거나 취소할 수 있나요?",
    answer:
      "예물은 개인 사이즈와 선택한 옵션에 맞춰 제작되는 경우가 많아 제작이 시작된 후에는 취소나 변경이 제한될 수 있습니다. 변경이 가능하더라도 제작 단계에 따라 추가 비용이나 위약금이 발생할 수 있습니다. 계약 전 디자인, 소재, 색상, 사이즈, 보석 등급, 각인 문구와 취소 규정을 꼼꼼히 확인해 주세요.",
  },
  {
    question: "반지 각인을 신청할 때 주의할 점이 있나요?",
    answer:
      "각인 문구는 제작이 시작된 뒤 변경하기 어렵거나 추가 비용이 발생할 수 있습니다. 이름과 날짜, 영문 철자, 대소문자, 기호가 정확한지 계약서 작성 시 다시 확인해 주세요. 반지의 폭과 디자인에 따라 각인 가능한 글자 수와 위치가 제한될 수 있습니다.",
  },
  {
    question: "예물샵은 하루에 몇 군데 정도 방문하는 것이 좋나요?",
    answer:
      "한 업체당 상담과 착용에 약 1시간에서 1시간 30분 정도 소요될 수 있어 하루 2~3곳 정도가 적당합니다. 너무 많은 업체를 연속으로 방문하면 디자인이 비슷하게 느껴지거나 판단이 어려워질 수 있습니다. 업체별 가격, 착용감, 디자인, 제작 기간, AS 조건을 메모해 두면 비교하는 데 도움이 됩니다.",
  },
  {
    question: "예물 계약 시 어떤 내용을 확인해야 하나요?",
    answer:
      "최종 디자인과 소재, 금 함량, 중량, 보석 종류와 등급, 사이즈, 제작 기간, 수령일을 확인해야 합니다. 계약금과 잔금 결제 시점, 추가 비용, 디자인 변경 및 취소 규정, AS 범위도 함께 확인하는 것이 좋습니다. 상담 내용과 사은품, 서비스 품목은 구두 안내에만 의존하지 말고 계약서에 기재해 주세요.",
  },
];

const hanbokSource = [
  {
    question: "한복 상담을 받으려면 방문 예약이 필요한가요?",
    answer:
      "한복 상담은 원활한 피팅과 상담을 위해 방문 전에 예약하는 것을 권장합니다. 특히 주말이나 혼주 한복을 함께 상담하는 경우에는 상담 인원과 필요한 시간을 미리 전달하는 것이 좋습니다. 예약 시 예식일과 촬영일, 신랑·신부 한복 또는 혼주 한복 상담 여부를 함께 알려주세요.",
  },
  {
    question: "한복 업체를 방문하기 전에 무엇을 준비하면 좋나요?",
    answer:
      "예식일과 촬영일, 필요한 한복 종류, 대여 또는 맞춤 여부, 예상 예산을 미리 정리해 두면 상담이 수월합니다. 원하는 색상이나 분위기의 참고 사진이 있다면 함께 준비하는 것도 좋습니다. 혼주 한복을 상담하는 경우에는 양가 어머님의 키와 평소 사이즈, 선호하는 색상 등을 미리 확인해 주세요.",
  },
  {
    question: "한복 상담 시 사진을 촬영해도 되나요?",
    answer:
      "매장과 디자인에 따라 사진 촬영이 제한될 수 있으므로 촬영 전에 직원에게 확인하는 것이 좋습니다. 촬영이 가능한 경우에는 착용한 한복의 색상과 디자인, 견적 내용을 함께 기록해 두면 업체나 구성을 비교할 때 도움이 됩니다. 다른 고객이나 직원이 사진에 포함되지 않도록 주의해 주세요.",
  },
  {
    question: "한복 방문 예약을 변경하거나 취소할 수 있나요?",
    answer:
      "예약 일정 변경과 취소 가능 여부는 업체별 규정에 따라 다릅니다. 예약금이 있는 경우 취소 시점에 따라 일부 비용이 반환되지 않을 수 있으므로 예약 전에 변경 및 취소 규정을 확인해 주세요. 방문이 어려워진 경우에는 가능한 한 빠르게 업체에 연락해 일정을 조정하는 것이 좋습니다.",
  },
  {
    question: "한복 계약 후 취소하거나 구성을 변경할 수 있나요?",
    answer:
      "한복은 개인의 체형과 선택한 원단, 디자인에 맞춰 준비되는 경우가 많아 제작이나 발주가 시작된 후에는 취소와 변경이 제한될 수 있습니다. 변경이 가능하더라도 진행 단계에 따라 추가 비용이나 위약금이 발생할 수 있습니다. 계약 전 대여 또는 맞춤 여부, 구성, 원단, 색상, 수령 일정과 취소·환불 규정을 계약서에서 확인해 주세요.",
  },
];

const faqList: Record<'suit' | 'hanbok' | 'jewelry', FaqItem[]> = {
  suit: createFaqList(suitSource, suitKeywords),
  hanbok: createFaqList(hanbokSource, hanbokKeywords),
  jewelry: createFaqList(jewelrySource, jewelryKeywords),
}
export default faqList
